'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import useSWR from 'swr';
import { serializeKey } from '@/utils/swr/keys';

export interface ProductRowPaginationOptions {
  initialRows: number;
  loadMoreText: string;
  cacheKey?: string;
  onInitialLoadComplete?: () => void;
}

export function normalizeProductRows(value: unknown): number {
  const rows = typeof value === 'number' || typeof value === 'string' ? Number(value) : 0;
  return Number.isSafeInteger(rows) && rows > 0 ? rows : 1;
}

/** Keeps fetched pages separate from the rows the user has chosen to reveal. */
export function useProductRowPagination({
  options,
  isWap,
  itemCount,
  loading,
  hasMore,
  hasAttemptedFetch,
  hasFetchError = false,
  loadMore,
}: {
  options?: ProductRowPaginationOptions;
  isWap: boolean;
  itemCount: number;
  loading: boolean;
  hasMore: boolean;
  hasAttemptedFetch: boolean;
  hasFetchError?: boolean;
  loadMore: () => void | Promise<void>;
}) {
  const instanceId = useId();
  const enabled = !!options;
  const initialRows = normalizeProductRows(options?.initialRows);
  const { data: rows = initialRows, mutate: mutateRows } = useSWR<number>(
    enabled
      ? serializeKey(['local', 'data', 'productRows', options.cacheKey || instanceId, initialRows])
      : null,
    null,
    {
      fallbackData: initialRows,
      revalidateOnMount: false,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const [grid, setGrid] = useState<HTMLDivElement | null>(null);
  const [columns, setColumns] = useState(isWap ? 2 : 1);
  const [measured, setMeasured] = useState(isWap || !enabled);
  const requestedRef = useRef('');
  const notifiedRef = useRef(false);
  const interactionLockedRef = useRef(false);
  const [pendingRows, setPendingRows] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || isWap || !grid) return;
    const measure = () => {
      const tracks = window.getComputedStyle(grid).gridTemplateColumns.trim();
      setColumns(tracks && tracks !== 'none' ? tracks.split(/\s+/).length : 1);
      setMeasured(true);
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(grid);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, grid, isWap]);

  const visibleCount = enabled ? rows * (isWap ? 2 : columns) : itemCount;
  const targetCount = (pendingRows ?? rows) * (isWap ? 2 : columns);
  const needsPage = enabled && measured && hasAttemptedFetch && itemCount < targetCount && hasMore;

  useEffect(() => {
    if (!needsPage || loading || hasFetchError) return;
    const requestKey = `${targetCount}:${itemCount}`;
    if (requestedRef.current === requestKey) return;
    requestedRef.current = requestKey;
    void loadMore();
  }, [hasFetchError, itemCount, loadMore, loading, needsPage, targetCount]);

  useEffect(() => {
    if (loading) return;
    if (hasFetchError) {
      interactionLockedRef.current = false;
    } else if (!needsPage) {
      if (pendingRows !== null) {
        void mutateRows(pendingRows, { revalidate: false });
        setPendingRows(null);
      }
      interactionLockedRef.current = false;
    }
  }, [hasFetchError, loading, mutateRows, needsPage, pendingRows]);

  useEffect(() => {
    const emptySettled = itemCount === 0 && (!hasMore || hasFetchError);
    if (
      !enabled ||
      (!measured && !emptySettled) ||
      !hasAttemptedFetch ||
      loading ||
      (needsPage && !hasFetchError) ||
      notifiedRef.current
    )
      return;
    notifiedRef.current = true;
    options?.onInitialLoadComplete?.();
  }, [
    enabled,
    hasAttemptedFetch,
    hasFetchError,
    hasMore,
    itemCount,
    loading,
    measured,
    needsPage,
    options,
  ]);

  const showMore = useCallback(() => {
    if (interactionLockedRef.current || loading) return;
    interactionLockedRef.current = true;
    if (hasFetchError) {
      requestedRef.current = '';
      if (pendingRows === null && itemCount >= visibleCount) setPendingRows(rows + 2);
      void loadMore();
    } else {
      setPendingRows(rows + 2);
    }
  }, [hasFetchError, itemCount, loadMore, loading, pendingRows, rows, visibleCount]);

  return {
    gridRef: setGrid,
    visibleCount,
    showMore,
    hasMoreRows: enabled && (itemCount > visibleCount || hasMore || hasFetchError),
    loadingRows: loading || (needsPage && !hasFetchError),
  };
}
